import { types as api } from '@mesa-labs/mesa-api';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import * as types from '../../types';

export type RevenueState = {
  beginningAt: api.ISODateString;
  endingAt: api.ISODateString;
  periodization: api.TimeSeriesPeriodization;
  enforcePeriodization?: boolean;
  revenueDateAnchoring: api.RevenueDateAnchoring;
  vendorSummaryTimeSeriesDisplay: types.SummaryTimeSeriesDisplay;
  vendorClient?: api.ClientResponse;
  clientSummaryTimeSeriesDisplay: types.SummaryTimeSeriesDisplay;
  partnerSummaryTimeSeriesDisplay: types.SummaryTimeSeriesDisplay;
  runRateTrailingDays: number;
};

export const initialState: RevenueState = {
  beginningAt: DateTime.now().minus({ month: 6 }).startOf('month').toISO(),
  endingAt: DateTime.now().plus({ month: 1 }).endOf('month').toISO(),
  periodization: api.TimeSeriesPeriodization.MONTH,
  enforcePeriodization: false,
  revenueDateAnchoring: api.RevenueDateAnchoring.FUNDED_DATE,
  vendorSummaryTimeSeriesDisplay: types.SummaryTimeSeriesDisplay.Actual,
  vendorClient: undefined,
  clientSummaryTimeSeriesDisplay: types.SummaryTimeSeriesDisplay.Actual,
  partnerSummaryTimeSeriesDisplay: types.SummaryTimeSeriesDisplay.Actual,
  runRateTrailingDays: 30
};

const revenueSlice = createSlice({
  name: 'revenue',
  initialState,
  reducers: {
    updateBeginningAt(state: RevenueState, action: PayloadAction<api.ISODateString>) {
      state.beginningAt = action.payload;
    },
    updateEndingAt(state: RevenueState, action: PayloadAction<api.ISODateString>) {
      state.endingAt = action.payload;
    },
    updatePeriodization(state: RevenueState, action: PayloadAction<api.TimeSeriesPeriodization>) {
      state.periodization = action.payload;
    },
    updateEnforcePeriodization(state: RevenueState, action: PayloadAction<boolean>) {
      state.enforcePeriodization = action.payload;
    },
    updateRevenueDateAnchoring(state: RevenueState, action: PayloadAction<api.RevenueDateAnchoring>) {
      state.revenueDateAnchoring = action.payload;
    },
    updateVendorSummaryTimeSeriesDisplay(state: RevenueState, action: PayloadAction<types.SummaryTimeSeriesDisplay>) {
      state.vendorSummaryTimeSeriesDisplay = action.payload;
    },
    updateVendorClient(state: RevenueState, action: PayloadAction<api.ClientResponse | undefined>) {
      state.vendorClient = action.payload;
    },
    updateClientSummaryTimeSeriesDisplay(state: RevenueState, action: PayloadAction<types.SummaryTimeSeriesDisplay>) {
      state.clientSummaryTimeSeriesDisplay = action.payload;
    },
    updatePartnerSummaryTimeSeriesDisplay(state: RevenueState, action: PayloadAction<types.SummaryTimeSeriesDisplay>) {
      state.partnerSummaryTimeSeriesDisplay = action.payload;
    },
    updateRunRateTrailingDays(state: RevenueState, action: PayloadAction<number>) {
      state.runRateTrailingDays = action.payload;
    },
  },
});

export const {
  updateBeginningAt,
  updateEndingAt,
  updatePeriodization,
  updateEnforcePeriodization,
  updateRevenueDateAnchoring,
  updateVendorSummaryTimeSeriesDisplay,
  updateVendorClient,
  updateClientSummaryTimeSeriesDisplay,
  updatePartnerSummaryTimeSeriesDisplay,
  updateRunRateTrailingDays
} = revenueSlice.actions;
export default revenueSlice.reducer;
